/* ==========================================================================
   Harper Reborn — shared countdown (product block `hp_countdown` and the
   `harper-countdown` section both use it).

   Root element contract:
     data-hp-countdown="<ISO datetime>"          fixed end (Liquid: date: '%Y-%m-%dT%H:%M:%S%z')
     data-hp-countdown-evergreen="<minutes>"     per-visitor timer instead of a fixed end
     data-hp-countdown-key="<storage key>"       where the evergreen end is remembered
     data-hp-countdown-expire="hide"             what happens at 00:00 (the root is hidden)
   Children: [data-hp-cd="d|h|m|s"] receive zero-padded numbers. The root gets
   `hidden` on expiry and dispatches `hp:countdown:end`. Evergreen timers never
   restart: once a visitor's timer ended it stays ended for that visitor.
   In the theme editor (Shopify.designMode) evergreen timers use sessionStorage
   so the merchant sees them restart on reload.
   The script may be injected again (quick-add modal re-runs the product
   section's scripts): a second copy just re-scans the document.
   ========================================================================== */
(function () {
  'use strict';
  if (window.HpCountdown) {
    window.HpCountdown.init(document);
    return;
  }

  var doc = document;
  var timers = [];

  function store() {
    try {
      return window.Shopify && window.Shopify.designMode ? window.sessionStorage : window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function read(key) {
    var s = store();
    if (!s) return null;
    try { return s.getItem(key); } catch (e) { return null; }
  }

  function write(key, val) {
    var s = store();
    if (!s) return;
    try { s.setItem(key, val); } catch (e) { /* private mode */ }
  }

  function parseIso(str) {
    if (!str) return NaN;
    // Liquid's %z gives +0300; Date wants +03:00.
    var norm = String(str).trim().replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    var t = Date.parse(norm);
    return isNaN(t) ? NaN : t;
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function resolveEnd(el) {
    var mins = parseFloat(el.dataset.hpCountdownEvergreen || '');
    if (!isNaN(mins) && mins > 0) {
      var key = el.dataset.hpCountdownKey || 'hp-cd';
      var saved = parseInt(read(key) || '', 10);
      if (saved === -1) return -1; // already finished for this visitor
      if (!saved || saved < 0 || isNaN(saved)) {
        saved = Date.now() + mins * 60000;
        write(key, String(saved));
      }
      return saved;
    }
    return parseIso(el.dataset.hpCountdown);
  }

  function bind(el) {
    if (el.dataset.hpCdBound) return;
    el.dataset.hpCdBound = '1';
    var out = {
      d: el.querySelector('[data-hp-cd="d"]'),
      h: el.querySelector('[data-hp-cd="h"]'),
      m: el.querySelector('[data-hp-cd="m"]'),
      s: el.querySelector('[data-hp-cd="s"]')
    };
    var end = resolveEnd(el);
    var evergreen = !!el.dataset.hpCountdownEvergreen;
    var iv = null;

    function finish() {
      if (iv) clearInterval(iv);
      iv = null;
      if (evergreen) write(el.dataset.hpCountdownKey || 'hp-cd', '-1');
      el.hidden = true;
      el.dispatchEvent(new CustomEvent('hp:countdown:end', { bubbles: true }));
    }

    function tick() {
      if (!el.isConnected) {
        // Element left the document (quick-add modal closed, section re-rendered).
        if (iv) clearInterval(iv);
        iv = null;
        return;
      }
      var left = Math.max(0, end - Date.now());
      var s = Math.floor(left / 1000);
      var d = Math.floor(s / 86400);
      var h = Math.floor((s % 86400) / 3600);
      var m = Math.floor((s % 3600) / 60);
      var sec = s % 60;
      if (out.d) out.d.textContent = pad(d);
      // Without a days slot, roll the days into the hours so nothing is lost.
      if (out.h) out.h.textContent = pad(out.d ? h : h + d * 24);
      if (out.m) out.m.textContent = pad(m);
      if (out.s) out.s.textContent = pad(sec);
      if (left <= 0) finish();
    }

    function start() {
      tick();
      iv = setInterval(tick, 1000);
      timers.push({ el: el, stop: function () { if (iv) clearInterval(iv); iv = null; } });
    }

    if (isNaN(end) || end === -1) {
      el.hidden = true;
      return;
    }
    if (end <= Date.now()) {
      finish();
      return;
    }
    start();
  }

  function init(scope) {
    scope = scope || doc;
    var list = scope.querySelectorAll ? scope.querySelectorAll('[data-hp-countdown],[data-hp-countdown-evergreen]') : [];
    [].slice.call(list).forEach(bind);
    if (scope.matches && (scope.matches('[data-hp-countdown]') || scope.matches('[data-hp-countdown-evergreen]'))) bind(scope);
  }

  // Stop timers whose element left the document (theme editor re-renders sections).
  doc.addEventListener('shopify:section:unload', function (e) {
    timers = timers.filter(function (t) {
      if (e.target && e.target.contains(t.el)) {
        t.stop();
        return false;
      }
      return true;
    });
  });
  doc.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { init(doc); });
  else init(doc);

  window.HpCountdown = { init: init };
})();
